import React from "react";
import { createRoot } from "react-dom/client";
import { SecondaryButton } from "@ucl/library";
import {
  FileExplorerToolbarItem,
  FlatFileExplorerToolbarItemTypes,
} from "./types";
import { faPlus } from "@fortawesome/free-solid-svg-icons";

export const renderFlatFileExplorerToolbarItems = <T = any>(
  toolbarItems: FileExplorerToolbarItem<T>[],
  selectedData?: T
): void => {
  toolbarItems.forEach((toolbarItem, index) => {
    const elementId = toolbarItem.id;
    if (!elementId) {
      console.warn(`Toolbar item at index ${index} missing id property`);
      return;
    }

    const element = document.getElementById(elementId);

    if (!element) {
      console.warn(
        `Element with id '${elementId}' not found in DOM for toolbar item`
      );
      return;
    }

    // Clear existing content
    element.innerHTML = "";

    // Render based on what properties are available
    if (
      toolbarItem.actionType ===
        FlatFileExplorerToolbarItemTypes.CustomButton ||
      toolbarItem.actionType === FlatFileExplorerToolbarItemTypes.NewFolder
    ) {
      const root = createRoot(element);
      root.render(
        React.createElement(SecondaryButton, {
          text: toolbarItem.text || "Button",
          minimal: toolbarItem.buttonSettings?.minimal ?? false,
          iconProps: toolbarItem.buttonSettings?.iconProps,
          disabled:
            toolbarItem.buttonSettings?.isDisabled?.(selectedData) ?? false,
          onClick: () => toolbarItem.buttonSettings?.onClick?.(selectedData),
        })
      );
    } else if (
      toolbarItem.actionType === FlatFileExplorerToolbarItemTypes.CustomText
    ) {
      // Display item
      const text = toolbarItem.textSettings?.getText?.(selectedData) || "";
      element.innerText = text;
    }
  });
};

// Default toolbar item configurations for FlatFileExplorer
export const getDefaultFlatFileExplorerToolbarItems = <T = any>(
  searchFolder?: (value: string, data?: T) => void,
  onNewFolderClick?: (data?: T) => void
): FileExplorerToolbarItem<T>[] => {
  const items: FileExplorerToolbarItem<T>[] = [];

  if (searchFolder) {
    items.push({
      id: "searchfolder",
      name: "searchfolder",
      template: `<span id="searchfolder" class="flat-file-explorer-toolbar-item"></span>`,
      actionType: FlatFileExplorerToolbarItemTypes.Search,
      align: "Left",
      searchSettings: {
        placeholder: "Search Folder Name",
        onSearch: searchFolder,
      },
    });
  }

  if (onNewFolderClick) {
    items.push({
      id: "newfolder",
      name: "newfolder",
      template: `<span id="newfolder" class="flat-file-explorer-toolbar-item"></span>`,
      actionType: FlatFileExplorerToolbarItemTypes.NewFolder,
      align: "Left",
      text: "New Folder",
      buttonSettings: {
        onClick: onNewFolderClick,
        iconProps: {
          icon: faPlus,
        },
        iconPosition: "left",
      },
    });
  }

  return items;
};

// Utility to combine FlatFileExplorer toolbar items with additional toolbar items
export const combineFlatFileExplorerToolbarItems = <T = any>(
  flatFileToolbarItems: FileExplorerToolbarItem<T>[],
  additionalToolbarItems: FileExplorerToolbarItem<T>[] = []
): FileExplorerToolbarItem<T>[] => {
  const result = [...flatFileToolbarItems, ...additionalToolbarItems];
  return result;
};
